import { db } from "../../firebase-config";
import { ref, push } from "firebase/database";
import CryptoJS from "crypto-js";

// Helper to get device/browser info and location (IP/location best effort)
async function getDetails(action) {
  const userAgent = navigator.userAgent || "Unknown";
  const deviceType = /Mobi|Android/i.test(userAgent) ? "Mobile" : "Desktop";
  let ip = "Unknown";
  let location = "Unknown";
  try {
    const resp = await fetch("https://ipapi.co/json/");
    const loc = await resp.json();
    ip = loc.ip || "Unknown";
    location = `${loc.city || "Unknown"}, ${loc.country_name || "Unknown"}`;
  } catch {}
  return {
    action,
    ip,
    browser: userAgent,
    device: deviceType,
    location,
  };
}

/**
 * Logs a 3D view event for a doctor in Firebase audit logs, matching admin log structure.
 * @param {Object} params - The log parameters.
 * @param {string} params.doctorName - The doctor's display name.
 * @param {string} params.patientId - The patient ID being viewed.
 * @param {string} params.patientName - The patient's name.
 * @param {string} params.modelUrl - The (decrypted) model URL.
 */
export const logDoctor3DView = async ({ doctorName, patientId, patientName, modelUrl }) => {
  if (!doctorName || !patientId || !modelUrl) return;
  const logRef = ref(db, 'audit_logs/UserManagement/doctor_logs/patientdicomview_doctor');
  const timestamp = new Date().toISOString();
  const secretKey = process.env.REACT_APP_SECRET_KEY || "default_key";
  const encryptedPatientId = CryptoJS.AES.encrypt(patientId.toString(), secretKey).toString();
  const encryptedModelUrl = CryptoJS.AES.encrypt(modelUrl, secretKey).toString();
  const details = await getDetails("3D model viewed by doctor");
  const logEntry = {

    doctor_name: doctorName,
    event_type: "3d_view_doctor",
    model_url: encryptedModelUrl,
    patient_id: encryptedPatientId,
    patient_name: patientName || "Unknown",
    success: true,
    timestamp,
    details,
  };
  push(logRef, logEntry);
};

/**
 * Logs a patient record view event for a doctor in Firebase audit logs, matching admin log structure.
 * @param {Object} params - The log parameters.
 * @param {string} params.doctorName - The doctor's display name.
 * @param {string} params.patientId - The patient ID being viewed.
 * @param {string} params.patientName - The patient's name.
 */
export const logDoctorPatientRecordView = async ({
  doctorName,
  patientId,
  patientName,
  success = true,
  failure_reason = ""
}) => {
  if (!doctorName || !patientId) return;
  const logRef = ref(db, 'audit_logs/UserManagement/doctor_logs/patient_record_view_doctor');
  const timestamp = new Date().toISOString();
  const secretKey = process.env.REACT_APP_SECRET_KEY || "default_key";
  const encryptedPatientId = CryptoJS.AES.encrypt(patientId.toString(), secretKey).toString();
  const details = await getDetails("Patient record viewed by doctor");
  const logEntry = {
    doctor_name: doctorName,
    event_type: "patient_record_view_doctor",
    patient_id: encryptedPatientId,
    patient_name: patientName || "Unknown",
    success,
    timestamp,
    details,
  };
  if (!success && failure_reason) {
    logEntry.failure_reason = failure_reason;
  }
  push(logRef, logEntry);
};

/**
 * Logs a report view event for a doctor in Firebase audit logs, matching admin log structure.
 * @param {Object} params - The log parameters.
 * @param {string} params.doctorName - The doctor's display name.
 * @param {string} params.patientId - The patient ID being viewed.
 * @param {string} params.patientName - The patient's name.
 * @param {string} params.reportUrl - The (decrypted) report URL.
 * @param {boolean} [params.success] - Whether the event was successful.
 * @param {string} [params.failure_reason] - Reason for failure, if any.
 */
export const logDoctorViewReport = async ({
  doctorName,
  patientId,
  patientName,
  reportUrl,
  success = true,
  failure_reason = ""
}) => {
  if (!doctorName || !patientId || !reportUrl) return;
  const logRef = ref(db, 'audit_logs/UserManagement/doctor_logs/view_report_doctor');
  const timestamp = new Date().toISOString();
  const secretKey = process.env.REACT_APP_SECRET_KEY || "default_key";
  const encryptedPatientId = CryptoJS.AES.encrypt(patientId.toString(), secretKey).toString();
  const encryptedReportUrl = CryptoJS.AES.encrypt(reportUrl, secretKey).toString();
  const details = await getDetails("Report viewed by doctor");
  const logEntry = {
    doctor_name: doctorName,
    event_type: "view_report_doctor",
    patient_id: encryptedPatientId,
    patient_name: patientName || "Unknown",
    report_url: encryptedReportUrl,
    success,
    timestamp,
    details,
  };
  if (!success && failure_reason) {
    logEntry.failure_reason = failure_reason;
  }
  push(logRef, logEntry);
};

/**
 * Logs a report download event for a doctor in Firebase audit logs, matching admin log structure.
 * @param {Object} params - The log parameters.
 * @param {string} params.doctorName - The doctor's display name.
 * @param {string} params.patientId - The patient ID being viewed.
 * @param {string} params.patientName - The patient's name.
 * @param {string} params.reportUrl - The (decrypted) report URL.
 * @param {boolean} [params.success] - Whether the event was successful.
 * @param {string} [params.failure_reason] - Reason for failure, if any.
 */
export const logDoctorDownloadReport = async ({
  doctorName,
  patientId,
  patientName,
  reportUrl,
  success = true,
  failure_reason = ""
}) => {
  if (!doctorName || !patientId || !reportUrl) return;
  const logRef = ref(db, 'audit_logs/UserManagement/doctor_logs/download_report_doctor');
  const timestamp = new Date().toISOString();
  const secretKey = process.env.REACT_APP_SECRET_KEY || "default_key";
  const encryptedPatientId = CryptoJS.AES.encrypt(patientId.toString(), secretKey).toString();
  const encryptedReportUrl = CryptoJS.AES.encrypt(reportUrl, secretKey).toString();
  const details = await getDetails("Report downloaded by doctor");
  const logEntry = {
    doctor_name: doctorName,
    event_type: "download_report_doctor",
    patient_id: encryptedPatientId,
    patient_name: patientName || "Unknown",
    report_url: encryptedReportUrl,
    success,
    timestamp,
    details,
  };
  if (!success && failure_reason) {
    logEntry.failure_reason = failure_reason;
  }
  push(logRef, logEntry);
};

/**
 * Logs a 3D view action event for a doctor in Firebase audit logs, matching admin log structure.
 * @param {Object} params - The log parameters.
 * @param {string} params.doctorName - The doctor's display name.
 * @param {string} params.patientId - The patient ID being viewed.
 * @param {string} params.patientName - The patient's name.
 * @param {string} params.modelUrl - The (decrypted) 3D model URL.
 * @param {boolean} [params.success] - Whether the event was successful.
 * @param {string} [params.failure_reason] - Reason for failure, if any.
 */
export const logDoctor3DViewAction = async ({
  doctorName,
  patientId,
  patientName,
  modelUrl,
  success = true,
  failure_reason = ""
}) => {
  if (!doctorName || !patientId || !modelUrl) return;
  const logRef = ref(db, 'audit_logs/UserManagement/doctor_logs/3D_view_doctors');
  const timestamp = new Date().toISOString();
  const secretKey = process.env.REACT_APP_SECRET_KEY || "default_key";
  const encryptedPatientId = CryptoJS.AES.encrypt(patientId.toString(), secretKey).toString();
  const encryptedModelUrl = CryptoJS.AES.encrypt(modelUrl, secretKey).toString();
  const details = await getDetails("3D view action by doctor");
  const logEntry = {
    doctor_name: doctorName,
    event_type: "3D_view_doctors",
    patient_id: encryptedPatientId,
    patient_name: patientName || "Unknown",
    model_url: encryptedModelUrl,
    success,
    timestamp,
    details,
  };
  if (!success && failure_reason) {
    logEntry.failure_reason = failure_reason;
  }
  push(logRef, logEntry);
};

/**
 * Logs a prescription view event for a doctor in Firebase audit logs, matching admin log structure.
 * @param {Object} params - The log parameters.
 * @param {string} params.doctorName - The doctor's display name.
 * @param {string} params.patientId - The patient ID being viewed.
 * @param {string} params.patientName - The patient's name.
 * @param {string} params.prescriptionUrl - The (decrypted) prescription URL.
 * @param {boolean} [params.success] - Whether the event was successful.
 * @param {string} [params.failure_reason] - Reason for failure, if any.
 */
export const logDoctorViewPrescription = async ({
  doctorName,
  patientId,
  patientName,
  prescriptionUrl,
  success = true,
  failure_reason = ""
}) => {
  if (!doctorName || !patientId || !prescriptionUrl) return;
  const logRef = ref(db, 'audit_logs/UserManagement/doctor_logs/prescription_view_doctor');
  const timestamp = new Date().toISOString();
  const secretKey = process.env.REACT_APP_SECRET_KEY || "default_key";
  const encryptedPatientId = CryptoJS.AES.encrypt(patientId.toString(), secretKey).toString();
  const encryptedPrescriptionUrl = CryptoJS.AES.encrypt(prescriptionUrl, secretKey).toString();
  const details = await getDetails("Prescription viewed by doctor");
  const logEntry = {
    doctor_name: doctorName,
    event_type: "prescription_view_doctor",
    patient_id: encryptedPatientId,
    patient_name: patientName || "Unknown",
    prescription_url: encryptedPrescriptionUrl,
    success,
    timestamp,
    details,
  };
  if (!success && failure_reason) {
    logEntry.failure_reason = failure_reason;
  }
  push(logRef, logEntry);
};

/**
 * Logs a prescription upload event for a doctor in Firebase audit logs, matching admin log structure.
 * @param {Object} params - The log parameters.
 * @param {string} params.doctorName - The doctor's display name.
 * @param {string} params.patientId - The patient ID being viewed.
 * @param {string} params.patientName - The patient's name.
 * @param {string} params.prescriptionUrl - The (decrypted) prescription URL.
 * @param {boolean} [params.success] - Whether the event was successful.
 * @param {string} [params.failure_reason] - Reason for failure, if any.
 */
export const logDoctorUploadPrescription = async ({
  doctorName,
  patientId,
  patientName,
  prescriptionUrl,
  success = true,
  failure_reason = ""
}) => {
  if (!doctorName || !patientId || !prescriptionUrl) return;
  const logRef = ref(db, 'audit_logs/UserManagement/doctor_logs/prescription_upload_doctor');
  const timestamp = new Date().toISOString();
  const secretKey = process.env.REACT_APP_SECRET_KEY || "default_key";
  const encryptedPatientId = CryptoJS.AES.encrypt(patientId.toString(), secretKey).toString();
  const encryptedPrescriptionUrl = CryptoJS.AES.encrypt(prescriptionUrl, secretKey).toString();
  const details = await getDetails("Prescription uploaded by doctor");
  const logEntry = {
    doctor_name: doctorName,
    event_type: "prescription_upload_doctor",
    patient_id: encryptedPatientId,
    patient_name: patientName || "Unknown",
    prescription_url: encryptedPrescriptionUrl,
    success,
    timestamp,
    details,
  };
  if (!success && failure_reason) {
    logEntry.failure_reason = failure_reason;
  }
  push(logRef, logEntry);
}; 